import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { CTA, ConversionCTA, EASE, Eyebrow, Section, Stat, clipUp, enter, prefersReducedMotion } from "@/components/system";
import clientLogos from "@/data/client-logos.json";
import { HexField } from "@/components/HexField";
import { HeroFilm } from "@/components/HeroFilm";

/* ═══════════════════════════════════════════════════════════════════════════
   Hero.

   The old hero was a centred slogan with a neon glow and a scroll-driven 3D
   transform — the most generic thing on the site, and it pushed the actual
   work below the fold. A studio's most characteristic asset is its work, so
   the thesis here is a wall of real 9:16 frames sitting beside the promise.

   The hero rail and the selected work rail share the same featured proof pieces.
   ═══════════════════════════════════════════════════════════════════════════ */

// Real frames, recovered from the delivered cuts and committed to the repo.
// Only true 9:16 and square sources are used — landscape stills lose too much
// to a centre crop at this ratio.
const REEL_A = [
  { src: "/work/micro-drama-kitchen.jpg", client: "Micro Drama Player", kind: "Micro Drama", w: 281, h: 500 },
  { src: "/work/mazda.jpg", client: "Mazda", kind: "Brand Film", w: 540, h: 960 },
  { src: "/work/pocket-fm.jpg", client: "Pocket FM", kind: "Performance", w: 960, h: 960 },
  { src: "/work/jiohotstar.jpg", client: "JioHotstar", kind: "OTT Ad", w: 540, h: 960 },
  { src: "/cms/uploads/small_Kyrosity_Includ_Thumbnail_655e0a1115.png", client: "Includ", kind: "Performance", w: 281, h: 500, fallback: "/work/includ-kids-fits.png" },
  { src: "/cms/uploads/small_Kyrosity_Turing_Thumbnail_df94974513.png", client: "Turing", kind: "Performance", w: 281, h: 500, fallback: "/work/turing-frontier-fellows-vertical.jpg" },
  { src: "/cms/uploads/small_astrology_player_06e5d1e080.jpg", client: "Astrology Player", kind: "Performance", w: 281, h: 500, fallback: "/work/micro-drama-player.jpg" },
];

const REEL_B = [
  { src: "/cms/uploads/small_beauty_player_15efe427ea.jpg", client: "Beauty Player", kind: "Performance", w: 281, h: 500, fallback: "/work/pokus-ai.jpg" },
  { src: "/cms/uploads/small_micro_drama_us_player_bc4b69ed41.jpg", client: "US Micro Drama", kind: "Micro Drama", w: 500, h: 281, fallback: "/work/micro-drama-player.jpg" },
  { src: "/cms/uploads/small_micro_drama_hindi_show_d44302d77f.jpg", client: "Hindi Micro Drama", kind: "Micro Drama", w: 281, h: 500, fallback: "/work/mr-hellrocker.jpg" },
  { src: "/cms/uploads/small_micro_drama_vertical_0e7b6b445e.jpg", client: "Vertical Micro Drama", kind: "Micro Drama", w: 281, h: 500, fallback: "/work/dashverse.jpg" },
  { src: "/cms/uploads/small_regional_farmer_fd27319baa.jpg", client: "Regional Performance Content", kind: "Regional Content", w: 281, h: 500, fallback: "/work/educational-ai-influencer.jpg" },
];

const MOBILE_REEL = [...REEL_A, ...REEL_B];

/* ── Client marks ───────────────────────────────────────────────────────────
   The nine logos used to arrive in four different treatments — a full-colour
   gradient tile, a colour illustration, a flat mark, and (for three of them)
   an INVENTED wordmark set in the site's own typeface next to a real logotype.
   They were also sized by bounding-box height, so a wide wordmark and a square
   mark at "the same size" looked nothing like the same size.

   All nine are normalised silhouettes now, painted with currentColor through
   their own alpha (see scripts/normalize_logos.py), and their box comes from
   the manifest, where width/height were solved for equal INK AREA. The only
   thing this file decides is the accessible name. */
const LOGO_NAMES = {
  "dashverse.png": "Dashverse",
  "includ.png": "Includ",
  "india-tv.png": "India TV",
  "jiohotstar.png": "JioHotstar",
  "mosaic-wellness.png": "Mosaic Wellness",
  "pocket-fm.png": "Pocket FM",
  "pokus-ai.png": "Pokus AI",
  "silo-fortune.png": "Silo Fortune",
  "turing.png": "Turing",
};

const PROOF_BRANDS = clientLogos.map(({ file, src, w, h }) => ({
  src,
  w,
  h,
  name: LOGO_NAMES[file] ?? file.replace(/\.[a-z]+$/, ""),
}));

/* ── Client logo rail ───────────────────────────────────────────────────────
   A sliding rail that you can actually slide.

   The original was a CSS marquee — `translateX(-50%)` on a track inside
   `overflow: hidden`. Three problems came from that one choice: the column is
   only 362px against a 1709px track so three of nine marks were ever on
   screen; `overflow: hidden` meant no drag, swipe or trackpad scroll could
   reach the rest; and the reduced-motion rule killed the animation without
   restoring any other way through, freezing those readers on the same three
   permanently.

   The fix is to drive the loop with `scrollLeft` on a genuinely scrollable
   element instead of transforming a clipped one. Native scrolling then works
   for free — drag, swipe, shift-wheel, keyboard — and the auto-drift is only an
   enhancement layered on top. Interaction pauses the drift briefly so a swipe
   or keyboard input always gets control. Reduced motion switches the drift off
   and leaves a rail that still works.

   The list is rendered twice so the wrap is seamless; the second copy is
   aria-hidden. */
function useMarquee(speed = 26) {
  const ref = useRef(null);

  // Edge fades, but only on the edges there is actually more content past. A
  // static fade over the left edge dims the first logo while the rail is parked
  // at zero — which is precisely where a reduced-motion reader sits, for good.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const sync = () => {
      const max = el.scrollWidth - el.clientWidth;
      el.dataset.atStart = el.scrollLeft <= 2 ? "true" : "false";
      el.dataset.atEnd = el.scrollLeft >= max - 2 ? "true" : "false";
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync, { passive: true });
    return () => {
      el.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion()) return undefined;   // scrollable, just not self-moving

    let raf = 0;
    let last = 0;
    let pausedUntil = 0;

    // Any real interaction wins, and keeps winning for a moment afterwards —
    // otherwise the drift fights the reader's own scroll on the next frame.
    const hold = () => { pausedUntil = performance.now() + 1800; };
    const events = ["pointerdown", "wheel", "touchstart", "keydown"];
    events.forEach((e) => el.addEventListener(e, hold, { passive: true }));
    el.addEventListener("pointerenter", hold, { passive: true });
    el.addEventListener("focusin", hold);

    const step = (now) => {
      raf = requestAnimationFrame(step);
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      if (now < pausedUntil || document.hidden) return;
      el.scrollLeft += speed * dt;
      // The track holds two identical copies, so rewinding by exactly half is
      // invisible.
      const half = el.scrollWidth / 2;
      if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
    };
    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      events.forEach((e) => el.removeEventListener(e, hold));
      el.removeEventListener("pointerenter", hold);
      el.removeEventListener("focusin", hold);
    };
  }, [speed]);

  return ref;
}

/* ── Frame ──────────────────────────────────────────────────────────────────
   The stills ARE the product, so nothing is painted over them. Each of the 22
   tiles used to carry three softening devices at once: a 20% veil, a
   bottom-half scrim, and an overlay caption in 9px gold — on top of the wall's
   own mask. Two of the four visible tiles read as empty black rectangles.

   The image now runs at full opacity with no overlay of any kind. Where the
   metadata is worth reading (the phone row) it is a caption below the frame,
   in normal flow, at full contrast.

   shrink-0 is load-bearing: without it the flex column compresses the frames
   past their 9:16 ratio and the wall reads as ragged. */
function Frame({ item, priority = false, decorative = false }) {
  return (
    <div className="reel-frame shrink-0 border border-line">
      <img
        src={item.src}
        alt={decorative ? "" : `${item.client} — ${item.kind}`}
        width={item.w}
        height={item.h}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority && item.client === "Mazda" ? "high" : "auto"}
        decoding="async"
        /* Three of these frames come from the CMS. Without a fallback, a CMS
           hiccup renders the tile as an empty bordered rectangle — which is
           exactly the "half the portfolio is blank boxes" first impression the
           scrim fix was meant to end. Showcase already did this; the hero,
           which is seen first, did not. */
        onError={(e) => {
          if (item.fallback && !e.target.src.endsWith(item.fallback)) {
            e.target.src = item.fallback;
          }
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </div>
  );
}

/* Two columns drifting in opposite directions. Duplicated once so the
   translateY(-50%) loop is seamless; the copy is aria-hidden so screen readers
   don't hear every frame twice. */
function ReelColumn({ items, direction, priority }) {
  return (
    <div className={`flex flex-col gap-4 ${direction}`}>
      {items.map((item, i) => (
        <Frame key={item.src} item={item} priority={priority && i < 2} />
      ))}
      <div aria-hidden="true" className="contents">
        {items.map((item) => (
          <Frame key={`${item.src}-loop`} item={item} decorative />
        ))}
      </div>
    </div>
  );
}

/* ── Pointer depth ──────────────────────────────────────────────────────────
   The scene leans away from the cursor. Two normalised motion values (-1..1)
   fed through a slow spring, so the layers settle rather than track — a
   layer that follows the pointer exactly reads as a cursor effect, one that
   arrives a beat late reads as mass.

   Fine pointers only. On touch there is no hover, so the listener would only
   ever fire on a tap that was meant for something else. */
function usePointerDepth(enabled) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 38, damping: 16, mass: 0.8 });
  const sy = useSpring(my, { stiffness: 38, damping: 16, mass: 0.8 });

  useEffect(() => {
    if (!enabled) return undefined;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 2);
      my.set((e.clientY / window.innerHeight - 0.5) * 2);
    };
    const onLeave = () => { mx.set(0); my.set(0); };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, [enabled, mx, my]);

  return { sx, sy };
}

export const Hero = () => {
  const logoRail = useMarquee();

  /* ── Depth choreography ─────────────────────────────────────────────────
     Three inputs drive the layers behind the content, and the content itself
     never moves except with the page:

       scroll   the dive. Leaving the hero, the wordmark recedes (scales up,
                thins to nothing) while the bloom rushes forward (scales up,
                brightens). Reading top to bottom you pass THROUGH the scene
                rather than past it — the same beat as the reference, without
                a camera.
       pointer  the lean. Bloom follows the cursor a little, the wordmark
                counter-moves a little less, so the two layers separate and the
                depth between them becomes visible.
       mount    the reveal. The whole stack fades up over a second, after the
                headline has already landed, so the words are never waiting on
                the scenery.

     Hooks run unconditionally. Under reduced motion every style is withheld
     and the layers are painted once and left still. */
  const stage = useRef(null);
  const reduced = prefersReducedMotion();

  // "pending" until the film slot reports. The hex mesh stays painted until a
  // real film is confirmed playable, so a missing or slow asset never leaves
  // the hero on a bare plane.
  const [film, setFilm] = useState("pending");
  const { scrollYProgress } = useScroll({
    target: stage,
    offset: ["start start", "end start"],
  });
  const { sx, sy } = usePointerDepth(!reduced);

  // Wordmark: furthest back. Least scroll travel, counter-leans the pointer.
  const wordmarkY = useTransform([scrollYProgress, sy], ([p, y]) => `${p * -22 + y * -1.2}%`);
  const wordmarkX = useTransform(sx, [-1, 1], ["1.4%", "-1.4%"]);
  const wordmarkScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const wordmarkOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  // Bloom: nearest. Most scroll travel, leans with the pointer.
  const bloomY = useTransform([scrollYProgress, sy], ([p, y]) => `${p * 14 + y * 2.2}%`);
  const bloomX = useTransform(sx, [-1, 1], ["-2.6%", "2.6%"]);
  const bloomScale = useTransform(scrollYProgress, [0, 1], [1, 1.28]);

  const dustOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
  <>
    <section
      ref={stage}
      id="hero"
      data-reel-label="Top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-ground pb-16 pt-28 md:pb-24 md:pt-32"
    >
      {/* ── Depth ──
          Behind everything, reachable by nothing. */}
      <motion.div
        aria-hidden="true"
        className="hero-depth"
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
      >
        <motion.div
          className="hero-bloom-wrap"
          style={reduced ? undefined : { x: bloomX, y: bloomY, scale: bloomScale }}
        >
          <div className="hero-bloom" />
        </motion.div>
        <HeroFilm onReady={() => setFilm("ready")} onMissing={() => setFilm("missing")} />
        {film !== "ready" && <HexField />}
        <motion.span
          className="hero-wordmark font-display"
          style={reduced ? undefined : { x: wordmarkX, y: wordmarkY, scale: wordmarkScale, opacity: wordmarkOpacity }}
        >
          Kyrosity
        </motion.span>
        <motion.div className="hero-dust" style={reduced ? undefined : { opacity: dustOpacity }} />
        <div className="hero-vignette" />
      </motion.div>

      {/* z-10 is load-bearing: the depth stack is positioned, so without a
          stacking order of its own the grid would paint underneath it. */}
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:px-10 lg:grid-cols-12 lg:items-center lg:gap-12">
        {/* ── Promise ── */}
        <div className="min-w-0 lg:col-span-6">
          <motion.div {...enter(0)}>
            <Eyebrow className="mb-7">Global performance creative / India / US / beyond</Eyebrow>
          </motion.div>

          {/* Each line rides up from behind its own baseline. The display step
              sets leading at 0.92, so the overflow-hidden wrapper needs a little
              top padding or the cap-height clips. */}
          <h1 className="font-display text-display uppercase text-bone">
            <span className="block overflow-hidden pt-1">
              <motion.span {...clipUp(0)} className="block">Find the ads</motion.span>
            </span>
            <span className="block overflow-hidden pt-1">
              <motion.span {...clipUp(1)} className="block">worth</motion.span>
            </span>
            {/* This line used to be violet. A headline is not clickable, so it
                does not get an accent — the line break is the emphasis. */}
            <span className="block overflow-hidden pt-1">
              <motion.span {...clipUp(2)} className="block">scaling.</motion.span>
            </span>
          </h1>

          <motion.p {...enter(3)} className="measure mt-7 font-body text-body-l text-mute">
            Performance creative that has <strong className="font-medium text-bone">cut customer acquisition cost by 50%</strong> and opened new audiences for micro drama platforms and D2C brands, including Pocket FM, Mosaic Wellness, and Turing.
          </motion.p>

          {/* The secondary button used to carry a play glyph in a bordered chip
              — the strongest "this will open a video" signal in the hero, on a
              control that only scrolls down the page. It gets the same arrow as
              every other link now, because that is what it does. */}
          <motion.div {...enter(4)} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <ConversionCTA data-testid="hero-cta-book-call" />
            <CTA href="#showcase" tone="secondary" data-testid="hero-cta-see-work">
              Explore the work
            </CTA>
          </motion.div>

          {/* The offer, defined where the button that names it lives. This row
              was three nouns (Strategy / Production / Iteration) — the studio's
              process, which a visitor did not ask about. Every button on the
              site says "free sample" and nothing said what a sample was. */}
          <motion.div {...enter(5)} className="mt-10 max-w-md border-t border-rule pt-5">
            <p className="font-body text-caption text-mute">
              <span className="text-bone">The free sample:</span> one finished ad concept for your product. First cut in 48 hours. No fee, no commitment.
            </p>
          </motion.div>
        </div>

        {/* ── Work ── */}
        <motion.div
          {...enter(2)}
          /* min-w-0 is required, not cosmetic: grid items default to
             min-width:auto, so the scroller below sized the single mobile
             track to its max-content width and pushed the headline, lede and
             CTAs off-screen. */
          className="min-w-0 lg:col-span-6"
        >
          {/* Phones get a swipeable row — a drifting column reads as broken in
              a short viewport. This row was `hidden` with no `lg:hidden` pair
              while the wall was `hidden lg:grid`, so below 1024px the hero
              showed no work at all. */}
          <ul className="-mb-4 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 lg:hidden">
            {MOBILE_REEL.map((item, i) => (
              <li key={item.src} className="w-40 shrink-0 snap-start md:w-48">
                <Frame item={item} priority={i < 2} />
                <div className="mt-3">
                  <span className="block truncate font-body text-caption text-bone">{item.client}</span>
                  <span className="block truncate font-body text-caption text-faint">{item.kind}</span>
                </div>
              </li>
            ))}
          </ul>

          <div
            className="reel-wall hidden h-[27rem] grid-cols-2 gap-4 overflow-hidden lg:grid xl:h-[30rem]"
            style={{
              maskImage:
                "linear-gradient(to bottom, transparent, black 9%, black 91%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent, black 9%, black 91%, transparent)",
            }}
          >
            <ReelColumn items={REEL_A} direction="reel-up" priority />
            <ReelColumn items={REEL_B} direction="reel-down" />
          </div>
        </motion.div>
      </div>
    </section>

    {/* ── Signal rail ────────────────────────────────────────────────────────
        The proof band. The figure is the only coloured thing here and it is
        teal, because teal means "this is a measured fact" — the curve and its
        endpoint are drawn in the same token in index.css. */}
    <Section
      id="proof"
      data-reel-label="Proof"
      tone="deep"
      size="tight"
      className="signal-rail"
      containerClassName="grid gap-10 lg:grid-cols-[1.3fr_.7fr] lg:gap-12"
    >
      <div className="signal-story">
        <div className="signal-copy">
          <Eyebrow>Creative performance / signal</Eyebrow>
          <h2 className="mt-5 font-display text-h1 uppercase text-bone">
            Good creative<br />moves the curve.
          </h2>
          <p className="mt-4 font-body text-body text-mute">Lower acquisition cost, new audiences, and a first cut in 48 hours.</p>
        </div>

        <div className="signal-metric-wrap">
          <div className="signal-graph" aria-hidden="true">
            <svg viewBox="0 0 420 116" preserveAspectRatio="none">
              <polyline points="0,12 58,22 104,18 150,39 197,45 242,59 286,53 333,78 378,86 420,105" />
              <circle cx="420" cy="105" r="5" />
            </svg>
          </div>
          <Stat value="50%" label="Lower customer acquisition cost for performance clients" />
        </div>
      </div>

      <div className="client-logo-rail border-t border-rule pt-7 lg:flex lg:min-h-full lg:flex-col lg:justify-center lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
        <span className="font-label text-label uppercase text-faint">Teams we have worked with</span>
        <div
          ref={logoRail}
          className="client-logo-window mt-5"
          role="group"
          aria-label="Client logos, scrollable"
          tabIndex={0}
        >
          <ul className="client-logo-track">
            {[...PROOF_BRANDS, ...PROOF_BRANDS].map(({ src, w, h, name }, i) => (
              <li key={`${name}-${i}`} aria-hidden={i >= PROOF_BRANDS.length || undefined}>
                <span
                  className="client-logo"
                  style={{ "--logo": `url(${src})`, "--logo-w": `${w}px`, "--logo-h": `${h}px` }}
                  role="img"
                  aria-label={name}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  </>
  );
};

export default Hero;
